import api from "./client";
const TRUSTED_DEVICE_TOKEN_KEY = "mg_trusted_device_token";

function readTrustedDeviceToken() {
  try {
    return localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

function writeTrustedDeviceToken(token) {
  try {
    localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, token);
  } catch {}
}

export function clearTrustedDeviceToken() {
  try {
    localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
  } catch {}
}

export function getTrustedDeviceToken() {
  return readTrustedDeviceToken();
}

function generateTrustedDeviceToken() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `trusted-web-${crypto.randomUUID()}`;
    }
  } catch {}

  return `trusted-web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function ensureTrustedDeviceToken() {
  const existing = readTrustedDeviceToken();
  if (existing) return existing;

  const created = generateTrustedDeviceToken();
  writeTrustedDeviceToken(created);
  return created;
}

function detectDeviceType() {
  if (typeof navigator === "undefined") return "web";

  const agent = String(navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(agent)) return "tablet";
  if (/mobile|android|iphone|ipod/.test(agent)) return "mobile";
  return "web";
}

function detectOperatingSystem() {
  if (typeof navigator === "undefined") return "Unknown OS";

  const agent = String(navigator.userAgent || "").toLowerCase();
  if (agent.includes("windows")) return "Windows";
  if (agent.includes("mac os x") && !agent.includes("iphone") && !agent.includes("ipad")) {
    return "macOS";
  }
  if (agent.includes("android")) return "Android";
  if (agent.includes("iphone") || agent.includes("ipad") || agent.includes("ios")) return "iOS";
  if (agent.includes("linux")) return "Linux";
  return "Unknown OS";
}

function detectBrowserName() {
  if (typeof navigator === "undefined") return "Browser";

  const agent = String(navigator.userAgent || "").toLowerCase();
  if (agent.includes("edg/")) return "Edge";
  if (agent.includes("opr/") || agent.includes("opera")) return "Opera";
  if (agent.includes("chrome/") && !agent.includes("edg/") && !agent.includes("opr/")) {
    return "Chrome";
  }
  if (agent.includes("firefox/")) return "Firefox";
  if (agent.includes("safari/") && !agent.includes("chrome/")) return "Safari";
  return "Browser";
}

function buildDeviceName() {
  const deviceType = detectDeviceType();
  const os = detectOperatingSystem();
  const browser = detectBrowserName();

  if (deviceType === "mobile") return `${os} Phone - ${browser}`;
  if (deviceType === "tablet") return `${os} Tablet - ${browser}`;
  return `${os} PC - ${browser}`;
}

function generateMacAddress() {
  try {
    const stored = localStorage.getItem("mg_device_mac_address");
    if (stored) return stored;

    const parts = [
      navigator.userAgent || "",
      navigator.language || "",
      new Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      screen?.width || "",
      screen?.height || "",
      screen?.colorDepth || "",
      new Date().getTimezoneOffset(),
    ].join("|");

    const hash = btoa(parts)
      .replace(/[^a-zA-Z0-9]/g, "")
      .substring(0, 48)
      .toUpperCase();

    const mac = [
      hash.substring(0, 2),
      hash.substring(2, 4),
      hash.substring(4, 6),
      hash.substring(6, 8),
      hash.substring(8, 10),
      hash.substring(10, 12),
    ].join(":");

    localStorage.setItem("mg_device_mac_address", mac);
    return mac;
  } catch {
    return "";
  }
}

export function isCurrentDeviceTrusted(device) {
  const trustedToken = readTrustedDeviceToken();
  return Boolean(trustedToken) && String(device?.fcm_token || "") === trustedToken;
}

export async function fetchMyDevices() {
  try {
    const data = await api.get("/user/devices");
    return data?.data || data || [];
  } catch (error) {
    console.error("Devices fetch error:", error);
    return [];
  }
}

export async function registerDevice(deviceData) {
  try {
    const data = await api.post("/user/devices", deviceData);
    return data?.data || data;
  } catch (error) {
    console.error("Device registration error:", error);
    throw error;
  }
}

export async function trustCurrentDevice() {
  const trustedToken = ensureTrustedDeviceToken();
  const macAddress = generateMacAddress();
  const deviceMetadata = {
    device_name: buildDeviceName(),
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  };
  const devices = await fetchMyDevices();
  const existingDevice = Array.isArray(devices)
    ? devices.find((device) => String(device?.mac_address || device?.fcm_token || "") === (macAddress || trustedToken))
    : null;

  if (existingDevice?.id) {
    try {
      await updateDeviceActivity(existingDevice.id, deviceMetadata);
    } catch {}
    return { ...existingDevice, ...deviceMetadata };
  }

  return registerDevice({
    fcm_token: trustedToken,
    mac_address: macAddress,
    device_type: detectDeviceType(),
    ...deviceMetadata,
  });
}

export async function untrustCurrentDevice() {
  const trustedToken = readTrustedDeviceToken();
  if (!trustedToken) return false;

  try {
    const devices = await fetchMyDevices();
    const matches = Array.isArray(devices)
      ? devices.filter((device) => String(device?.fcm_token || "") === trustedToken)
      : [];

    for (const device of matches) {
      if (device?.id) {
        await deleteDevice(device.id);
      }
    }
  } finally {
    clearTrustedDeviceToken();
  }

  return true;
}

export async function deleteDevice(deviceId) {
  try {
    await api.delete(`/user/devices/${deviceId}`);
    return true;
  } catch (error) {
    console.error("Device deletion error:", error);
    throw error;
  }
}

export async function deleteAllDevices() {
  try {
    await api.delete("/user/devices/all");
    return true;
  } catch (error) {
    console.error("Delete all devices error:", error);
    throw error;
  }
}

export async function updateDeviceActivity(deviceId, payload = {}) {
  try {
    const data = await api.put(`/user/devices/${deviceId}/active`, payload);
    return data?.data || data;
  } catch (error) {
    console.error("Update device activity error:", error);
    throw error;
  }
}
